import { relations } from 'drizzle-orm';
import {
  boolean,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgTable,
  primaryKey,
  serial,
  timestamp,
  varchar,
} from 'drizzle-orm/pg-core';
import { account, type Provider } from './account';
import { authIntent } from './auth-intent';
import { organization } from './organization';

export const proxy = pgTable(
  'proxy',
  {
    id: serial().primaryKey(),
    active: boolean().notNull().default(true),
    country: varchar({ length: 2 }),
    host: varchar({ length: 255 }).notNull(),
    port: integer().notNull(),
    username: varchar({ length: 255 }),
    password: varchar({ length: 255 }),
    protocol: varchar({ length: 255 }).notNull(),
    proxy_provider: varchar({ length: 255 }),
    is_unipile: boolean().notNull().default(false),
  },
  (t) => [index('proxy_active_country_idx').on(t.active, t.country)],
);

export const proxysRelations = relations(proxy, ({ many }) => ({
  accounts: many(account),
  usages: many(proxyUsage),
  auth_intents: many(authIntent),
}));

export const PROXY_USAGE_EVENT_TYPE = ['update', 'proxy_failure', 'auth_failure', 'captcha_failure', 'captcha_success'] as const;

export type ProxyUsageEventType = (typeof PROXY_USAGE_EVENT_TYPE)[number];

export const proxyUsageEventTypeEnum = pgEnum('proxy_usage_event_type', PROXY_USAGE_EVENT_TYPE);

export const proxyUsage = pgTable(
  'proxy_usage',
  {
    proxy_id: integer()
      .notNull()
      .references(() => proxy.id, { onDelete: 'cascade' }),
    organization_id: integer().references(() => organization.id, { onDelete: 'set null' }),
    provider: varchar({ length: 255 }).notNull().$type<Provider>(),
    slots: integer().notNull(),
    last_update: timestamp({ withTimezone: true, mode: 'string' }),
    last_proxy_failure: timestamp({ withTimezone: true, mode: 'string' }),
    last_auth_failure: timestamp({ withTimezone: true, mode: 'string' }),
    last_captcha_failure: timestamp({ withTimezone: true, mode: 'string' }),
    last_captcha_success: timestamp({ withTimezone: true, mode: 'string' }),
  },
  (t) => [
    primaryKey({ columns: [t.proxy_id, t.provider] }),
    index('proxy_usage_provider_idx').on(t.provider),
    index('proxy_usage_organization_id_idx').on(t.organization_id),
  ],
);

export const proxyUsagesRelations = relations(proxyUsage, ({ many, one }) => ({
  proxy: one(proxy, { fields: [proxyUsage.proxy_id], references: [proxy.id] }),
  history: many(proxyUsageHistory),
}));

export const proxyUsageHistory = pgTable(
  'proxy_usage_history',
  {
    id: serial().primaryKey(),
    proxy_id: integer().notNull(),
    provider: varchar({ length: 255 }).notNull().$type<Provider>(),
    type: proxyUsageEventTypeEnum().notNull(),
    occurred_at: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
  },
  (t) => [
    index('proxy_usage_history_proxy_provider_occurred_at_idx').on(t.proxy_id, t.provider, t.occurred_at),
    index('proxy_usage_history_provider_type_occurred_at_idx').on(t.provider, t.type, t.occurred_at),
    foreignKey({
      columns: [t.proxy_id, t.provider],
      foreignColumns: [proxyUsage.proxy_id, proxyUsage.provider],
      name: 'proxy_usage_history_proxy_id_provider_fk',
    }).onDelete('cascade'),
  ],
);

export const proxyUsageHistoryRelations = relations(proxyUsageHistory, ({ one }) => ({
  usage: one(proxyUsage, {
    fields: [proxyUsageHistory.proxy_id, proxyUsageHistory.provider],
    references: [proxyUsage.proxy_id, proxyUsage.provider],
  }),
}));
